import { Component, Prop, h } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Rating component for SignNow brand user feedback
 *
 * @product signnow
 * @source htg
 * @version 1.0.1
 * @category feedback
 * @contentConfig Rating component for collecting user feedback with detailed content section
 * @slot sidebar - {sn-how-it-works}, optional - Place for how-it-works component in sidebar
 * @constant RATING_HEADING - "Rate your experience" - Rating widget heading
 * @constant RATING_DESCRIPTION - "Your feedback helps us improve and deliver the best possible service." - Rating widget description
 * @prop {object} values - {h2_1, p_2, h3_3, list_4, p_5, p_6} - Object containing structured content for the component
 */
@Component({
  tag: 'sn-rating-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNRatingType2 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * HTML tag for the rating heading element (h1, h2, h3, h4, h5, h6)
   * @default "h4"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h4';

  /**
   * Main heading text (h2)
   */
  @Prop({ attribute: 'h2_1', reflect: true }) h2_1?: string;

  /**
   * First paragraph text
   */
  @Prop({ attribute: 'p_2', reflect: true }) p_2?: string;

  /**
   * Subheading text (h3)
   */
  @Prop({ attribute: 'h3_3', reflect: true }) h3_3?: string;

  /**
   * List items array
   */
  @Prop({ mutable: true }) list_4?: string[] | string;

  /**
   * Second paragraph text
   */
  @Prop({ attribute: 'p_5', reflect: true }) p_5?: string;

  /**
   * Third paragraph text
   */
  @Prop({ attribute: 'p_6', reflect: true }) p_6?: string;

  /**
   * Raw HTML content rendered via innerHTML when no structured fields are provided.
   * Preferred over default slot to avoid Stencil hydration reordering issues.
   */
  @Prop({ attribute: 'raw_html', reflect: true }) raw_html?: string;

  /**
   * @dynamic
   * @type {object}
   * @property {string} h2_1 - Main heading
   * @property {string} p_2 - First paragraph
   * @property {string} h3_3 - Subheading
   * @property {string[]} list_4 - List items
   * @property {string} p_5 - Second paragraph
   * @property {string} p_6 - Third paragraph
   * @property {string} raw_html - Raw HTML content (rendered via innerHTML)
   */
  @Prop({ mutable: true }) values?:
    | {
        h2_1?: string;
        p_2?: string;
        h3_3?: string;
        list_4?: string[];
        p_5?: string;
        p_6?: string;
        raw_html?: string;
      }
    | string;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
    this.values = parseJsonProp(this.values);
    this.list_4 = parseJsonProp(this.list_4);
  }

  render() {
    const ratingHeading = getConstant(this.constants, 'RATING_HEADING', 'Rate your experience');
    // const ratingDescription = getConstant(this.constants, 'RATING_DESCRIPTION', 'Your feedback helps us improve and deliver the best possible service.');

    // Use individual props if available, otherwise fall back to values object
    const h2_1 = this.h2_1 ?? (this.values && typeof this.values !== 'string' ? this.values.h2_1 : undefined);
    const p_2 = this.p_2 ?? (this.values && typeof this.values !== 'string' ? this.values.p_2 : undefined);
    const h3_3 = this.h3_3 ?? (this.values && typeof this.values !== 'string' ? this.values.h3_3 : undefined);
    const list_4 = this.list_4 ?? (this.values && typeof this.values !== 'string' ? this.values.list_4 : undefined);
    const p_5 = this.p_5 ?? (this.values && typeof this.values !== 'string' ? this.values.p_5 : undefined);
    const p_6 = this.p_6 ?? (this.values && typeof this.values !== 'string' ? this.values.p_6 : undefined);
    const rawHtml = this.raw_html ?? (this.values && typeof this.values !== 'string' ? this.values.raw_html : undefined);

    const hasText = (value?: string): boolean => typeof value === 'string' && value.trim().length > 0;
    const normalizedList = Array.isArray(list_4) ? list_4.filter(item => typeof item === 'string' && item.trim().length > 0) : [];

    // Structured content should be considered "present" only when it has actual data.
    // This prevents empty values like list_4: [] from blocking raw_html rendering.
    const hasStructuredContent = hasText(h2_1) || hasText(p_2) || hasText(h3_3) || normalizedList.length > 0 || hasText(p_5) || hasText(p_6);
    const hasRawHtml = hasText(rawHtml);

    return (
      <me-section yGap="2xl" yGapLg="5xl" role="region" aria-label="Rating and Feedback">
        <me-row>
          <me-column span="12" spanMd="8">
            {hasStructuredContent ? (
              <div class="sn-rating-type-2__content">
                {hasText(h2_1) && (
                  <me-heading variant="2" tag="h2">
                    {h2_1}
                  </me-heading>
                )}
                {hasText(p_2) && <me-text tag="p">{p_2}</me-text>}
                {hasText(h3_3) && (
                  <me-heading variant="3" tag="h3">
                    {h3_3}
                  </me-heading>
                )}
                {normalizedList.length > 0 && <me-list items={normalizedList.map(item => ({ text: item }))} listType="plain"></me-list>}
                {hasText(p_5) && <me-text tag="p">{p_5}</me-text>}
                {hasText(p_6) && <me-text tag="p">{p_6}</me-text>}
              </div>
            ) : hasRawHtml ? (
              <div class="sn-rating-type-2__content" innerHTML={rawHtml}></div>
            ) : null}
          </me-column>
          <me-column span="12" spanMd="4">
            <slot name="sidebar"></slot>
            <rating-type-2
              averageScore="5.0"
              feedbackStatus="Satisfied"
              votesQuantity="1649 votes"
              notificationMessage="We appreciate your response!"
              triggerActions={['api:fetch-data']}
              actionData={{ userId: '112233' }}
            >
              <me-heading slot="heading" variant="5" tag={this.headingTag}>
                {ratingHeading}
              </me-heading>
              {/* <me-text slot="description">
                {ratingDescription}
              </me-text> */}
            </rating-type-2>
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
